import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import AdminHeader from '../components/AdminHeader';
import useAdminInstance from '../hooks/useAdminInstance';
import API_ENDPOINTS from '../config/api';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
  color: #fff;
`;

const Content = styled.div`
  padding: 32px 48px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: rgba(26, 26, 46, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 20px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.$color || '#fff'};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 32px 0 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(26, 26, 46, 0.4);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Td = styled.td`
  padding: 10px 16px;
  font-size: 14px;
  color: #ccc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Bar = styled.div`
  height: 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
  overflow: hidden;
  min-width: 80px;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${props => props.$color || '#0099ff'};
  border-radius: 3px;
  width: ${props => props.$width || '0%'};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$bg || 'rgba(0, 153, 255, 0.15)'};
  color: ${props => props.$color || '#0099ff'};
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #555;
  font-size: 15px;
`;

const WarningBanner = styled.div`
  background: rgba(255, 68, 68, 0.12);
  border: 1px solid rgba(255, 68, 68, 0.35);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #ff8888;
`;

// Keep in step with MODEL_PRICING_HISTORY in apps/backend/src/shared/modelPricing.js
// (.claude/rules/ai-models.md) — an unlisted model renders its raw ID in gray.
const MODEL_COLORS = {
  'claude-haiku-4-5-20251001': '#00cc88',
  'claude-sonnet-4-6': '#0099ff',
  'claude-opus-4-6': '#cc66ff',
  'claude-fable-5-1': '#ffaa33',
  'claude-haiku-5-5': '#33ddaa',
  'claude-sonnet-5-5': '#3377ff',
  'claude-opus-5-5': '#aa44ee',
};

const MODEL_SHORT = {
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-opus-4-6': 'Opus 4.6',
  'claude-fable-5-1': 'Fable',
  'claude-haiku-5-5': 'Haiku 5.5',
  'claude-sonnet-5-5': 'Sonnet 5.5',
  'claude-opus-5-5': 'Opus 5.5',
};

const PROMPT_COLORS = {
  'pivot': '#cc66ff',
  'solution': '#ff9900',
  'implementation': '#00cc88',
  'node': '#0099ff',
  'rag': '#ff6666',
};

function getPromptColor(key) {
  const prefix = key?.split('.')[0];
  return PROMPT_COLORS[prefix] || '#888';
}

export default function AiUsageDashboard() {
  const { instanceId } = useAdminInstance();

  const [stats, setStats] = useState(null);
  const [ragUsage, setRagUsage] = useState(null);
  const [recentLogs, setRecentLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = instanceId ? `?instanceId=${instanceId}` : '';
    Promise.all([
      fetch(API_ENDPOINTS.AI_LOG_STATS() + q, { credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch(API_ENDPOINTS.AI_LOG_RAG_USAGE() + q, { credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch(API_ENDPOINTS.AI_LOG() + q + (q ? '&' : '?') + 'limit=20', { credentials: 'include' }).then(r => r.json()).catch(() => null),
    ]).then(([s, r, l]) => {
      setStats(s);
      setRagUsage(r);
      setRecentLogs(l);
      setLoading(false);
    });
  }, [instanceId]);

  if (loading) return (
    <Container>
      <AdminHeader instanceId={instanceId} />
      <Content><EmptyState>Loading...</EmptyState></Content>
    </Container>
  );

  if (!stats?.summary?.total_calls) return (
    <Container>
      <AdminHeader instanceId={instanceId} />
      <Content><EmptyState>No AI calls logged yet. Run some analyses to see data here.</EmptyState></Content>
    </Container>
  );

  const { summary, byModel, byPrompt } = stats;
  const maxPromptCost = byPrompt?.[0]?.cost_usd || 1;
  const maxModelCost = byModel?.[0]?.cost_usd || 1;
  const unpricedModels = (byModel || []).filter(m => m.has_pricing === false).map(m => m.ai_model);
  const totalTokens = summary.total_tokens_input + summary.total_tokens_output
    + (summary.total_tokens_cache_read || 0) + (summary.total_tokens_cache_creation || 0);
  const maxCited = ragUsage?.mostCited?.[0]?.citations || 1;
  const maxRetrieved = ragUsage?.mostRetrieved?.[0]?.times_retrieved || 1;

  return (
    <Container>
      <AdminHeader instanceId={instanceId} />
      <Content>
        {summary.unpriced_calls > 0 && (
          <WarningBanner>
            {summary.unpriced_calls} calls have no price and are missing from Total Cost
            {unpricedModels.length > 0 && <> — model{unpricedModels.length > 1 ? 's' : ''} not in modelPricing.js: {unpricedModels.join(', ')}</>}.
            Add the pricing entry (both copies), then re-run the cost backfill (migration 282).
          </WarningBanner>
        )}
        {/* Summary stats */}
        <Grid>
          <StatCard>
            <StatValue $color="#0099ff">{summary.total_calls}</StatValue>
            <StatLabel>AI Calls</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#00cc88">${summary.total_cost_usd}</StatValue>
            <StatLabel>Total Cost</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#ff9900">{(totalTokens / 1000).toFixed(0)}K</StatValue>
            <StatLabel>Total Tokens (incl. cache)</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#cc66ff">{summary.unique_prompts}</StatValue>
            <StatLabel>Prompt Types</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color={summary.failed_calls > 0 ? '#ff4444' : '#00cc88'}>
              {summary.failed_calls}
            </StatValue>
            <StatLabel>Failed Calls</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#888">{(summary.avg_duration_ms / 1000).toFixed(1)}s</StatValue>
            <StatLabel>Avg Duration</StatLabel>
          </StatCard>
        </Grid>

        <TwoColumn>
          {/* By Model */}
          <div>
            <SectionTitle>Cost by Model</SectionTitle>
            <Table>
              <thead>
                <tr><Th>Model</Th><Th>Calls</Th><Th>Tokens</Th><Th>Cost</Th><Th></Th></tr>
              </thead>
              <tbody>
                {byModel?.map(m => (
                  <tr key={m.ai_model}>
                    <Td>
                      <Badge $bg={`${MODEL_COLORS[m.ai_model] || '#888'}22`} $color={MODEL_COLORS[m.ai_model] || '#888'}>
                        {MODEL_SHORT[m.ai_model] || m.ai_model}
                      </Badge>
                      {m.has_pricing === false && (
                        <Badge $bg="rgba(255, 68, 68, 0.15)" $color="#ff4444" style={{ marginLeft: 6 }}>
                          no price
                        </Badge>
                      )}
                    </Td>
                    <Td>{m.calls}</Td>
                    <Td>{(m.tokens / 1000).toFixed(0)}K</Td>
                    <Td>{m.cost_usd == null ? '—' : `$${m.cost_usd}`}</Td>
                    <Td>
                      <Bar>
                        <BarFill $color={MODEL_COLORS[m.ai_model]} $width={`${((m.cost_usd || 0) / maxModelCost) * 100}%`} />
                      </Bar>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* By Prompt */}
          <div>
            <SectionTitle>Cost by Prompt</SectionTitle>
            <Table>
              <thead>
                <tr><Th>Prompt</Th><Th>Calls</Th><Th>Avg ms</Th><Th>Cost</Th><Th></Th></tr>
              </thead>
              <tbody>
                {byPrompt?.map(p => (
                  <tr key={p.prompt_key}>
                    <Td>
                      <Badge $bg={`${getPromptColor(p.prompt_key)}22`} $color={getPromptColor(p.prompt_key)}>
                        {p.prompt_key}
                      </Badge>
                    </Td>
                    <Td>{p.calls}</Td>
                    <Td>{p.avg_ms ? `${(p.avg_ms / 1000).toFixed(1)}s` : '—'}</Td>
                    <Td>${p.cost_usd}</Td>
                    <Td>
                      <Bar>
                        <BarFill $color={getPromptColor(p.prompt_key)} $width={`${(p.cost_usd / maxPromptCost) * 100}%`} />
                      </Bar>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </TwoColumn>

        {/* RAG Sources */}
        {ragUsage && (
          <TwoColumn>
            <div>
              <SectionTitle>Top Cited Sources (used in AI responses)</SectionTitle>
              {ragUsage.mostCited?.length > 0 ? (
                <Table>
                  <thead>
                    <tr><Th>Source</Th><Th>Citations</Th><Th></Th></tr>
                  </thead>
                  <tbody>
                    {ragUsage.mostCited.slice(0, 15).map((s, i) => (
                      <tr key={i}>
                        <Td title={s.title}>{s.title?.substring(0, 80)}</Td>
                        <Td>{s.citations}</Td>
                        <Td>
                          <Bar>
                            <BarFill $color="#00cc88" $width={`${(s.citations / maxCited) * 100}%`} />
                          </Bar>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : <EmptyState>No citations yet</EmptyState>}
            </div>

            <div>
              <SectionTitle>Top Retrieved Sources (found by RAG)</SectionTitle>
              {ragUsage.mostRetrieved?.length > 0 ? (
                <Table>
                  <thead>
                    <tr><Th>Source</Th><Th>Retrieved</Th><Th>Avg Sim</Th><Th></Th></tr>
                  </thead>
                  <tbody>
                    {ragUsage.mostRetrieved.slice(0, 15).map((s, i) => (
                      <tr key={i}>
                        <Td title={s.title}>{s.title?.substring(0, 70)}</Td>
                        <Td>{s.times_retrieved}</Td>
                        <Td>{s.avg_similarity}</Td>
                        <Td>
                          <Bar>
                            <BarFill $color="#0099ff" $width={`${(s.times_retrieved / maxRetrieved) * 100}%`} />
                          </Bar>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : <EmptyState>No RAG retrievals yet</EmptyState>}
            </div>
          </TwoColumn>
        )}

        {/* Recent Calls */}
        <SectionTitle>Recent AI Calls</SectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>Time</Th>
              <Th>Prompt</Th>
              <Th>Model</Th>
              <Th>Tokens</Th>
              <Th>Cost</Th>
              <Th>Duration</Th>
              <Th>RAG</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {recentLogs?.logs?.map(log => (
              <tr key={log.id}>
                <Td>{new Date(log.timestamp).toLocaleString('fi-FI', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Td>
                <Td>
                  <Badge $bg={`${getPromptColor(log.prompt_key)}22`} $color={getPromptColor(log.prompt_key)}>
                    {log.prompt_key}
                  </Badge>
                </Td>
                <Td>
                  <Badge $bg={`${MODEL_COLORS[log.ai_model] || '#888'}22`} $color={MODEL_COLORS[log.ai_model] || '#888'}>
                    {MODEL_SHORT[log.ai_model] || log.ai_model?.split('-')[1]}
                  </Badge>
                </Td>
                <Td>{log.tokens_input + log.tokens_output}</Td>
                <Td>{log.estimated_cost_usd == null ? '—' : `$${log.estimated_cost_usd}`}</Td>
                <Td>{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '—'}</Td>
                <Td>{log.rag_results_count > 0 ? `${log.rag_results_count} docs` : '—'}</Td>
                <Td>
                  {log.success
                    ? <Badge $bg="rgba(0, 204, 136, 0.15)" $color="#00cc88">OK</Badge>
                    : <Badge $bg="rgba(255, 68, 68, 0.15)" $color="#ff4444">FAIL</Badge>
                  }
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Content>
    </Container>
  );
}
